import React, { useCallback } from 'react';
import { styled } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FieldInputProps } from 'formik';
import { CustomTextField } from './common.controls';
import t from '@/utils/translate';

const Combobox = styled(Autocomplete)({
  '& .MuiAutocomplete-endAdornment': {
    top: 'auto',
  }
});

const Revit3DViewRadioGroup = styled(RadioGroup)(({ gap }: { gap?: string }) => ({
  width: '350px',
  margin: '0',
  padding: '0px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: gap || '10px',
}));

const Revit3DViewLabel = styled(FormControlLabel)({
  height: '24px',
  margin: 0,
  padding: 0,

  ['& .MuiFormControlLabel-label']: {
    padding: '0px 4px',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '16px',
    color: '#6B778C',
  },
  [`
    & .MuiRadio-root.Mui-disabled,
    & .MuiRadio-root.Mui-checked.Mui-disabled,
    & .MuiRadio-root.Mui-disabled + .MuiFormControlLabel-label,
    & .MuiRadio-root.Mui-checked.Mui-disabled + .MuiFormControlLabel-label,
  `]: {
    color: '#6B778C',
  },
  [`
    & .MuiRadio-root.Mui-checked,
    & .MuiRadio-root.Mui-checked + .MuiFormControlLabel-label
  `]: {
    color: '#091E42',
  },
});

const Revit3DViewRadio = styled(Radio)({
  padding: '2px',
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
});

const Revit3DView = styled(CustomTextField)({
  margin: '0 0 0 30px',

  '& .MuiInputBase-root': {
    width: '320px',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#C1C7D0',
  }
});

type Props = {
  views: string[];
  value: string | null;
  onValueChange: (v: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | boolean;
  specificViewOptionLabel?: string;
  gap?: string;
} & FieldInputProps<any>;

export default ({
  views,
  value,
  disabled = false,
  onValueChange,
  error = false,
  helperText,
  specificViewOptionLabel = t('project.settings.option.specificView'),
  gap,
  ...inputProps
}: Props) => {
  const onRevit3DViewRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    let v;
    if ((event.target as HTMLInputElement).value === 'true') {
      v = null;
    } else {
      v = views.length ? views[0] : '';
    }
    if (v !== value) {
      onValueChange(v);
    }
  }, [ value, onValueChange ]);

  const onRevit3DViewAutocompleteChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, v?: string) => {
    if (typeof v === 'undefined') {
      v = event.target.value;
    }
    if (v !== value) {
      onValueChange(v || '');
    }
  }, [ value, onValueChange ]);

  return (
    <Revit3DViewRadioGroup
      value={value === null}
      onChange={onRevit3DViewRadioChange}
      gap={gap}
    >
      <Revit3DViewLabel disabled={disabled} value={true} control={<Revit3DViewRadio />} label={t('project.settings.option.defaultView')} />
      <Revit3DViewLabel disabled={disabled} value={false} control={<Revit3DViewRadio />} label={specificViewOptionLabel} />
      {(value !== null && !!views.length) && <Combobox
        disabled={disabled}
        {...inputProps}
        value={value}
        options={views}
        renderInput={
          (params) => <Revit3DView
            {...params}
            value={value}
            label={t('project.settings.label.specifyView')}
            error={error}
            helperText={helperText}
            variant="outlined"
          />
        }
        onChange={onRevit3DViewAutocompleteChange}
        onInputChange={onRevit3DViewAutocompleteChange}
      />}
      {(value !== null && !views.length) && <Revit3DView
        {...inputProps}
        disabled={disabled}
        label={t('project.settings.label.specifyView')}
        error={error}
        helperText={helperText}
        variant="outlined"
        onChange={onRevit3DViewAutocompleteChange}
        value={value}
      />}
    </Revit3DViewRadioGroup>
  );
}